import { Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";

export default function About() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <Helmet>
        <title>About Free1099Calc - Understanding 1099 Taxes for Independent Contractors</title>
        <meta
          name="description"
          content="Free1099Calc helps U.S. freelancers, contractors, and self-employed professionals understand 1099 taxes, quarterly payments, deductions, and self-employment tax."
        />
        <meta name="keywords" content="1099 tax calculator, self-employment tax, quarterly tax payments, freelancer taxes, independent contractor taxes, estimated tax calculator, business deductions, SE tax" />
        <link rel="canonical" href="https://free1099calc.com/about" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="About Free1099Calc - 1099 Tax Help for Freelancers" />
        <meta property="og:description" content="Learn how Free1099Calc helps freelancers and independent contractors estimate taxes, understand quarterly payments, and maximize deductions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/about" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="About Free1099Calc - 1099 Tax Calculator" />
        <meta name="twitter:description" content="Free tools for freelancers to estimate taxes, plan quarterly payments, and understand self-employment tax." />
      </Helmet>

      {/* Expense Tracker Banner */}
      <div className="mb-8">
        <ExpenseTrackerBanner compact />
      </div>

      <h1 className="text-3xl font-bold mb-6">About Free1099Calc</h1>
      
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-semibold mb-3">Our Mission</h2>
          <p className="text-slate-700 mb-4">
            Free1099Calc was built to make tax estimation simple, accurate, and accessible for freelancers, 
            independent contractors, and small business owners across the United States. As independent 
            professionals ourselves, we understand how stressful and confusing it can be to estimate taxes 
            when you don't have an employer withholding them for you.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Understanding 1099 Income</h2>
          <p className="text-slate-700 mb-4">
            When you work as an independent contractor, freelancer, or self-employed professional, you receive 
            <strong> Form 1099</strong> instead of a W-2. This means you're responsible for calculating and 
            paying your own taxes throughout the year. Unlike traditional employees, no taxes are automatically 
            withheld from your payments—which means you need to plan ahead and set aside money for:
          </p>
          
          <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
            <li><strong>Federal income tax</strong> - Based on your total taxable income and tax bracket</li>
            <li><strong>Self-employment tax</strong> - 15.3% covering Social Security (12.4%) and Medicare (2.9%)</li>
            <li><strong>State income tax</strong> - Varies by state, with some states having no income tax</li>
            <li><strong>Local taxes</strong> - Some cities and counties impose additional taxes</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">How Self-Employment Taxes Work</h2>
          <p className="text-slate-700 mb-4">
            The biggest surprise for new independent contractors is <strong>self-employment (SE) tax</strong>. 
            When you're a traditional employee, your employer pays half of your Social Security and Medicare 
            taxes. But when you're self-employed, you pay both halves—the full 15.3%.
          </p>
          
          <p className="text-slate-700 mb-4">Here's the breakdown:</p>
          
          <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
            <li><strong>12.4%</strong> for Social Security on earnings up to $168,600 (2024 limit)</li>
            <li><strong>2.9%</strong> for Medicare on all earnings</li>
            <li><strong>Additional 0.9%</strong> Medicare tax on earnings over $200,000 (single) or $250,000 (married)</li>
          </ul>
          
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
            <p className="text-sm text-slate-700">
              <strong>Good news:</strong> You can deduct half of your SE tax (the "employer" portion) when calculating 
              your adjusted gross income, which reduces your overall tax burden.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Quarterly Estimated Tax Payments</h2>
          <p className="text-slate-700 mb-4">
            The IRS requires you to pay taxes as you earn income throughout the year. If you expect to owe 
            $1,000 or more in taxes, you must make quarterly estimated tax payments on these dates:
          </p>
          
          <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
            <li><strong>Q1:</strong> April 15 (for income earned January 1 - March 31)</li>
            <li><strong>Q2:</strong> June 15 (for income earned April 1 - May 31)</li>
            <li><strong>Q3:</strong> September 15 (for income earned June 1 - August 31)</li>
            <li><strong>Q4:</strong> January 15 of the following year (for income earned September 1 - December 31)</li>
          </ul>
          
          <p className="text-slate-700 mb-4">
            Missing these payments can result in penalties and interest charges. Use our{" "}
            <Link to="/" className="text-blue-600 hover:underline">1099 tax calculator</Link> to estimate 
            your quarterly payments and avoid surprises.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Common Business Deductions</h2>
          <p className="text-slate-700 mb-4">
            One advantage of being self-employed is the ability to deduct legitimate business expenses. 
            Common deductions include:
          </p>
          
          <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
            <li><strong>Home office deduction</strong> - If you use part of your home exclusively for business</li>
            <li><strong>Equipment and supplies</strong> - Computers, software, office furniture, and supplies</li>
            <li><strong>Internet and phone</strong> - Business portion of your bills</li>
            <li><strong>Business mileage</strong> - 67 cents per mile in 2024</li>
            <li><strong>Health insurance premiums</strong> - If you're self-employed and pay for your own coverage</li>
            <li><strong>Retirement contributions</strong> - SEP IRA, Solo 401(k), or SIMPLE IRA contributions</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Who Should Use Free1099Calc</h2>
          <p className="text-slate-700 mb-4">
            Our calculator is designed for anyone earning income reported on Form 1099, including:
          </p>
          
          <ul className="list-disc pl-6 mb-4 space-y-1 text-slate-700">
            <li>Freelancers and consultants (writers, designers, developers, marketers)</li>
            <li>Independent contractors (construction workers, drivers, handymen)</li>
            <li>Gig economy workers (Uber, Lyft, DoorDash, Instacart)</li>
            <li>Online sellers (Etsy, Amazon FBA, eBay)</li>
            <li>Content creators (YouTubers, podcasters, influencers)</li>
            <li>Small business owners (sole proprietors and single-member LLCs)</li>
            <li>Anyone with side income alongside a W-2 job</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">How Our Calculator Works</h2>
          <p className="text-slate-700 mb-4">
            Free1099Calc follows IRS guidelines and current tax tables to provide accurate estimates. 
            Our calculator considers:
          </p>
          
          <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-700">
            <li>Your total 1099 income and any W-2 income</li>
            <li>Business expenses and deductions</li>
            <li>Self-employment tax calculation (both halves of FICA)</li>
            <li>Federal income tax based on 2024 tax brackets</li>
            <li>State income tax for all 50 states</li>
            <li>Standard deduction</li>
            <li>Retirement contributions (SEP IRA, Solo 401k)</li>
            <li>Home office deduction using the simplified method</li>
            <li>Health insurance premiums for the self-employed</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Tax Planning Tips</h2>
          <div className="space-y-4">
            <div className="bg-green-50 border-l-4 border-green-500 p-4">
              <p className="font-semibold mb-2">Set Aside 25-30% of Every Payment</p>
              <p className="text-sm text-slate-700">
                As a general rule, set aside 25-30% of every payment you receive for taxes. High earners 
                in high-tax states may need to save 35-40%. Use a separate savings account to avoid spending this money.
              </p>
            </div>
            
            <div className="bg-green-50 border-l-4 border-green-500 p-4">
              <p className="font-semibold mb-2">Track Everything</p>
              <p className="text-sm text-slate-700">
                Keep detailed records of all income and expenses. Use accounting software like QuickBooks, 
                FreshBooks, or a simple spreadsheet. Save receipts and document business mileage.
              </p>
            </div>
            
            <div className="bg-green-50 border-l-4 border-green-500 p-4">
              <p className="font-semibold mb-2">Maximize Retirement Contributions</p>
              <p className="text-sm text-slate-700">
                Contributing to a SEP IRA or Solo 401(k) reduces your taxable income while building retirement 
                savings. You can contribute up to 25% of your net self-employment income.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-3">Additional Resources</h2>
          <p className="text-slate-700 mb-4">
            Visit our <Link to="/faq" className="text-blue-600 hover:underline">FAQ page</Link> for 
            answers to common questions about 1099 taxes and quarterly payments.
          </p>
          
          <p className="text-slate-700 mb-4">For official IRS guidance, consult:</p>
          
          <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-700">
            <li>
              <a href="https://www.irs.gov/businesses/small-businesses-self-employed/self-employed-individuals-tax-center" 
                 target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                IRS Self-Employed Individuals Tax Center
              </a>
            </li>
            <li>
              <a href="https://www.irs.gov/businesses/small-businesses-self-employed/estimated-taxes" 
                 target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                IRS Estimated Taxes Guide
              </a>
            </li>
            <li>
              <a href="https://www.irs.gov/forms-pubs/about-schedule-se-form-1040" 
                 target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Schedule SE (Self-Employment Tax)
              </a>
            </li>
          </ul>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-6">
            <p className="font-semibold mb-2">⚠️ Important Disclaimer</p>
            <p className="text-sm text-slate-700">
              Free1099Calc is an educational tool that provides estimates based on publicly available 
              IRS tax tables. It is not a substitute for professional tax advice. Always consult a qualified 
              CPA, enrolled agent, or tax attorney for personalized guidance.
            </p>
          </div>
        </section>

        <RecentPosts />
      </div>
    </div>
  );
}



